/**
 * Area — Verifica identità (PR-ONB-PREAUTH-4).
 *
 * Once the backoffice completes the preliminary check the request moves to
 * PREAUTHORIZED_FOR_IDENTITY and the candidate can start / resume their
 * identity verification HERE — from their authenticated area. This is the ONLY
 * surface that starts a Didit session for the candidate (the backoffice never
 * opens one on their behalf).
 *
 * Before PREAUTHORIZED the page keeps the informational "not yet available"
 * copy. The start flow reuses the canonical fresh-token contract
 * (`performAreaIdentityStart`: reloadUser → getIdToken(true) → /me → POST start)
 * and the provider-agnostic view-model (`deriveIdentityVerificationView`).
 */

import { useRef, useState } from 'react';
import { Banner } from '@ab-platform/ui/banner';
import { FieldError } from '@ab-platform/ui/field-error';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { PillButton } from '@ab-platform/ui/pill-button';
import { SoftBadge } from '@ab-platform/ui/soft-badge';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import { useAreaMe } from '@/pages/area/areaMeContext';
import { getAreaMe, startAreaIdentityVerification } from '@/lib/areaApi';
import { performAreaIdentityStart } from '@/lib/areaIdentityStartFlow';
import {
  ensureStableController,
  type AreaIdentityStartController,
} from '@/lib/areaIdentityStartController';
import { deriveIdentityVerificationView } from '@/lib/identity-verification-view';
import type { KycNormalizedStatus } from '@/lib/api';

export function AreaIdentityPage(): JSX.Element {
  const session = useAreaSession();
  const { me, refresh } = useAreaMe();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The controller owns the real busy flag + double-click guard; on a REDIRECT
  // it deliberately does NOT clear busy (the browser is navigating away), so the
  // button stays disabled until the page unloads. Lazily created ONCE and kept
  // on the ref so it survives re-renders (a fresh controller would reset busy).
  const controllerRef = useRef<AreaIdentityStartController | null>(null);
  const controller = ensureStableController(controllerRef);

  const request = me.request;
  const iv = me.identityVerification;
  const isPreauthorized =
    request?.status === 'PREAUTHORIZED_FOR_IDENTITY' && iv !== null;

  // Before the BO preauthorizes the request, keep the informational copy.
  if (!isPreauthorized || !iv) {
    return (
      <FocusedPageShell
        title="Verifica identità"
        description="La verifica dell'identità del referente è l'ultimo passaggio prima della valutazione finale."
      >
        <Banner tone="info" title="Non ancora disponibile">
          La verifica identità sarà disponibile dopo il controllo preliminare
          dei dati e dei documenti caricati da parte di Aurino Broker. Ti
          avviseremo quando potrai procedere.
        </Banner>
      </FocusedPageShell>
    );
  }

  const status = iv.normalizedStatus as KycNormalizedStatus;
  const view = deriveIdentityVerificationView(status);

  const handleStart = (): void => {
    // The controller guards double-clicks and keeps `busy` set on REDIRECT.
    void controller.start({
      performStart: () =>
        performAreaIdentityStart({
          reloadUser: () => session.reloadUser(),
          getToken: (force) => session.getToken(force),
          getMe: (token) => getAreaMe(token),
          start: (token) => startAreaIdentityVerification(token),
        }),
      navigate: (url) => window.location.assign(url),
      onBusyChange: setBusy,
      onError: setError,
      onRefresh: refresh,
    });
  };

  return (
    <FocusedPageShell
      title="Verifica identità"
      description="Il controllo preliminare è stato completato. Verifica la tua identità per proseguire."
    >
      <div className="grid gap-4">
        <Banner tone="success" title="Controllo preliminare completato">
          Ora verifica la tua identità. La verifica viene svolta tramite un
          fornitore esterno. La registrazione non è ancora conclusa: l'esito
          sarà sottoposto ai controlli di Aurino Broker.
        </Banner>

        <div className="grid gap-2 rounded-2xl border border-border p-4">
          <div className="flex items-center gap-2">
            <SoftBadge tone={view.tone === 'neutral' ? 'neutral' : view.tone}>
              {view.badgeLabel}
            </SoftBadge>
          </div>
          <p className="text-sm text-muted-foreground">{view.description}</p>

          <div className="flex flex-wrap gap-2 pt-1">
            {view.cta ? (
              <PillButton
                type="button"
                size="sm"
                disabled={busy}
                onClick={() => void handleStart()}
              >
                {busy ? 'Attendere…' : view.cta.label}
              </PillButton>
            ) : null}
            {view.showRefresh ? (
              <PillButton
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => refresh()}
              >
                Aggiorna stato
              </PillButton>
            ) : null}
          </div>
        </div>

        {error ? <FieldError message={error} /> : null}
      </div>
    </FocusedPageShell>
  );
}
